import { useState } from "react";
import { CalendarClock, X } from "lucide-react";
import { fmtP, heatCell } from "./Momentum";

const todayIso = () => new Date().toISOString().slice(0, 10);
const lastDay = (m) => `${m}-${String(new Date(+m.slice(0, 4), +m.slice(5, 7), 0).getDate()).padStart(2, "0")}`;
const fmtDay = (s) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—");

// Month | W1 | W2 | W3 | W4 — the running week is marked "(current)"; plus the secondary "View as of…" look-back.
export function PeriodControl({ period, setPeriod, weeks, month, asOf, setAsOf }) {
  const [pickOpen, setPickOpen] = useState(false);
  const maxAsOf = lastDay(month) < todayIso() ? lastDay(month) : todayIso();
  const items = [{ key: "month", label: "Month" }, ...(weeks || []).map((w) => ({ key: w.week, label: w.week, current: w.current, title: `${w.week} · ${w.start}–${w.end}` }))];
  return (
    <div className="flex flex-col items-start gap-1">
      <div className="flex items-center rounded-md border overflow-hidden text-[11px]" data-testid="ov-period-control">
        {items.map((it) => (
          <button key={it.key} onClick={() => setPeriod(it.key)} title={it.title} className={`px-2.5 py-1.5 font-semibold transition-colors whitespace-nowrap ${period === it.key ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary"}`} data-testid={`ov-period-${it.key}`}>
            {it.label}{it.current && <span className={`ml-1 text-[9px] font-normal ${period === it.key ? "text-background/80" : "text-amber-700"}`} data-testid="ov-period-current-mark">(current)</span>}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 text-[10px]">
        {!pickOpen && !asOf && <button onClick={() => setPickOpen(true)} className="inline-flex items-center gap-1 text-muted-foreground underline-offset-2 hover:underline" data-testid="ov-asof-link"><CalendarClock size={11} /> View as of…</button>}
        {(pickOpen || asOf) && (
          <>
            <input type="date" value={asOf} min={`${month}-01`} max={maxAsOf} onChange={(e) => { setAsOf(e.target.value); setPickOpen(false); }} className="h-6 rounded border bg-background px-1.5 text-[10px]" data-testid="ov-asof-input" />
            {!asOf && <button onClick={() => setPickOpen(false)} className="text-muted-foreground hover:text-foreground" data-testid="ov-asof-cancel"><X size={11} /></button>}
          </>
        )}
      </div>
    </div>
  );
}

export function AsOfBanner({ asOf, onClear }) {
  if (!asOf) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="ov-asof-banner">
      <CalendarClock size={14} />
      <span>Viewing as of <b data-testid="ov-asof-banner-date">{fmtDay(asOf)}</b> — the page is frozen at that date (plans, achievement, momentum and ranks).</span>
      <button onClick={onClear} className="ml-auto inline-flex items-center gap-1 rounded border border-amber-400 bg-white px-2 py-0.5 font-semibold hover:bg-amber-100" data-testid="ov-asof-clear"><X size={11} /> Clear</button>
    </div>
  );
}

// Week view only: compact W1–W4 Retail Ach % row so the month context stays visible (selected week highlighted).
export function WeekSummary({ weeks, selected, onPick }) {
  return (
    <div className="rounded-md border bg-card px-3 py-2 flex flex-wrap items-center gap-2 text-xs" data-testid="ov-week-summary">
      <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mr-1">Retail Ach % by week</span>
      {weeks.map((w) => {
        const c = w.cells.retail;
        const sel = w.week === selected;
        return (
          <button key={w.week} onClick={() => onPick(w.week)} className={`inline-flex items-center gap-1.5 rounded border px-2 py-1 transition-colors ${sel ? "border-foreground ring-1 ring-foreground/40 bg-secondary" : "hover:bg-secondary/60"} ${w.future ? "text-muted-foreground" : ""}`} title={`${w.week} · ${w.start}–${w.end}`} data-testid={`ov-week-summary-${w.week}`}>
            <span className="font-heading font-bold">{w.week}</span>
            {w.current && <span className="rounded bg-amber-200 text-amber-900 px-1 text-[9px] font-bold uppercase">now</span>}
            <span className={`num font-semibold rounded px-1 ${w.future ? "" : heatCell(c.pct)}`} data-testid={`ov-week-summary-${w.week}-pct`}>{w.future ? "—" : fmtP(c.pct)}</span>
            <span className="text-[10px] text-muted-foreground num">{w.future ? `plan ${c.plan ?? "—"}` : `${c.ach ?? 0} / ${c.plan ?? "—"}`}</span>
          </button>
        );
      })}
    </div>
  );
}
